import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { refundService } from '../services/refundService';
import ConfirmDialog from '../components/ConfirmDialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';

const RefundManagement = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  const [filters, setFilters] = useState({
    status: '',
    userId: '',
    orderId: '',
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [action, setAction] = useState(null); // 'approve', 'reject', 'process'
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user || (user.role !== 'agent' && user.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }
    loadRefunds();
  }, [user, filters]);

  const loadRefunds = async () => {
    try {
      setLoading(true);
      const response = await refundService.getAllRefunds(filters);
      if (response.success) {
        setRefunds(response.refunds || []);
        setPagination({
          total: response.total || 0,
          totalPages: response.totalPages || 0,
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (refundId) => {
    try {
      setProcessing(true);
      const response = await refundService.approveRefund(refundId);
      if (response.success) {
        toast.success('Remboursement approuvé avec succès');
        setSelectedRefund(null);
        setAction(null);
        loadRefunds();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.warning('Veuillez fournir une raison de rejet');
      return;
    }

    try {
      setProcessing(true);
      const response = await refundService.rejectRefund(selectedRefund._id, rejectionReason);
      if (response.success) {
        toast.success('Remboursement rejeté');
        setSelectedRefund(null);
        setAction(null);
        setRejectionReason('');
        loadRefunds();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcess = async (refundId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Traiter le remboursement',
      message: 'Êtes-vous sûr de vouloir traiter ce remboursement ? Cette action est irréversible.',
      type: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          setProcessing(true);
          const response = await refundService.processRefund(refundId);
          if (response.success) {
            toast.success(`Remboursement traité avec succès. Transaction ID: ${response.transactionId}`);
            setSelectedRefund(null);
            setAction(null);
            loadRefunds();
          } else {
            toast.error(`Erreur: ${response.error}`);
          }
        } catch (error) {
          console.error('Erreur:', error);
          toast.error('Une erreur est survenue');
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      approved: '#28a745',
      rejected: '#dc3545',
      processed: '#17a2b8',
      cancelled: '#6c757d',
    };
    return colors[status] || '#6c757d';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      processed: 'Traité',
      cancelled: 'Annulé',
    };
    return labels[status] || status;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <Navbar />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="container" style={{ maxWidth: '1400px', margin: '40px auto', padding: '20px' }}>
        <h1 style={{ marginBottom: '30px', color: '#FF0000', fontSize: '32px', fontWeight: '700' }}>
          💰 Gestion des Remboursements
        </h1>

        {/* Filtres */}
        <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="rejected">Rejeté</option>
                <option value="processed">Traité</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                ID Commande
              </label>
              <input
                type="text"
                value={filters.orderId}
                onChange={(e) => setFilters(prev => ({ ...prev, orderId: e.target.value, page: 1 }))}
                placeholder="Rechercher par ID commande"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>
                Total: <strong>{pagination.total}</strong> demande(s)
              </span>
            </div>
          </div>
        </div>

        {/* Liste des remboursements */}
        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Chargement...</p>
          </div>
        ) : refunds.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666' }}>Aucune demande de remboursement</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
              {refunds.map((refund) => (
                <div key={refund._id} className="card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                          Commande: {refund.orderId?.orderNumber || refund.orderId}
                        </h3>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: getStatusColor(refund.status),
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}>
                          {getStatusLabel(refund.status)}
                        </span>
                      </div>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Client:</strong> {refund.userId?.firstName} {refund.userId?.lastName} ({refund.userId?.email})
                      </p>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Montant:</strong> {formatCurrency(refund.amount)}
                      </p>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Raison:</strong> {refund.reason}
                      </p>
                      {refund.description && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                          <strong>Description:</strong> {refund.description}
                        </p>
                      )}
                      {refund.rejectionReason && (
                        <p style={{ margin: '5px 0', color: '#dc3545', fontSize: '14px' }}>
                          <strong>Raison du rejet:</strong> {refund.rejectionReason}
                        </p>
                      )}
                      {refund.processedBy && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '12px' }}>
                          <strong>Traité par:</strong> {refund.processedBy?.firstName} {refund.processedBy?.lastName}
                        </p>
                      )}
                      <p style={{ margin: '5px 0', color: '#999', fontSize: '12px' }}>
                        Créé le: {formatDate(refund.createdAt)}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                      {refund.status === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedRefund(refund);
                              setAction('approve');
                            }}
                            className="btn btn-primary"
                            style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                          >
                            Approuver
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRefund(refund);
                              setAction('reject');
                            }}
                            className="btn"
                            style={{
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              backgroundColor: '#dc3545',
                              color: '#fff',
                            }}
                          >
                            Rejeter
                          </button>
                        </>
                      )}
                      {refund.status === 'approved' && (
                        <button
                          onClick={() => handleProcess(refund._id)}
                          className="btn"
                          style={{
                            fontSize: '14px',
                            whiteSpace: 'nowrap',
                            backgroundColor: '#17a2b8',
                            color: '#fff',
                          }}
                        >
                          Traiter le remboursement
                        </button>
                      )}
                      <Link
                        to={`/tracking/${refund.orderId?._id || refund.orderId}`}
                        className="btn"
                        style={{
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          backgroundColor: '#6c757d',
                          color: '#fff',
                          textDecoration: 'none',
                          display: 'inline-block',
                          textAlign: 'center',
                        }}
                      >
                        Voir la commande
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '30px',
              }}>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={filters.page === 1}
                  className="btn btn-secondary"
                >
                  ← Précédent
                </button>
                <span style={{ color: '#666', fontSize: '14px' }}>
                  Page <strong>{filters.page}</strong> sur <strong>{pagination.totalPages}</strong>
                </span>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={filters.page >= pagination.totalPages}
                  className="btn btn-secondary"
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal d'approbation */}
      {action === 'approve' && selectedRefund && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => {
            setSelectedRefund(null);
            setAction(null);
          }}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              Approuver le remboursement
            </h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Êtes-vous sûr de vouloir approuver ce remboursement de {formatCurrency(selectedRefund.amount)} ?
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleApprove(selectedRefund._id)}
                className="btn btn-primary"
                disabled={processing}
              >
                {processing ? 'Traitement...' : 'Approuver'}
              </button>
              <button
                onClick={() => {
                  setSelectedRefund(null);
                  setAction(null);
                }}
                className="btn btn-secondary"
                disabled={processing}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rejet */}
      {action === 'reject' && selectedRefund && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => {
            setSelectedRefund(null);
            setAction(null);
            setRejectionReason('');
          }}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              Rejeter le remboursement
            </h2>
            <div className="form-group">
              <label htmlFor="rejectionReason">Raison du rejet *</label>
              <textarea
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="4"
                required
                placeholder="Expliquez pourquoi ce remboursement est rejeté..."
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleReject}
                className="btn"
                disabled={processing || !rejectionReason.trim()}
                style={{
                  backgroundColor: '#dc3545',
                  color: '#fff',
                }}
              >
                {processing ? 'Traitement...' : 'Rejeter'}
              </button>
              <button
                onClick={() => {
                  setSelectedRefund(null);
                  setAction(null);
                  setRejectionReason('');
                }}
                className="btn btn-secondary"
                disabled={processing}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default RefundManagement;

